import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { SectionCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CreditCard, CalendarClock, CheckCircle2, AlertTriangle, Ban, Loader2 } from "lucide-react";

/** ISO tarih string'ini "DD.MM.YYYY" formatına döner. */
function fmtDate(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return "—"; }
}

const PLAN_LABELS = { monthly: "Aylık", yearly: "Yıllık" };
const PLAN_PRICES = {
  monthly: "400 TL + KDV (480 TL/ay)",
  yearly: "4.000 TL + KDV (4.800 TL/yıl)",
};

const STATUS_META = {
  trial:               { label: "Ücretsiz Deneme",    tone: "amber",   icon: CalendarClock },
  trial_expired:       { label: "Deneme Sona Erdi",    tone: "rose",    icon: AlertTriangle },
  paid:                { label: "Aktif Abonelik",      tone: "emerald", icon: CheckCircle2 },
  cancel_at_period_end:{ label: "İptal Bekliyor",       tone: "amber",   icon: CalendarClock },
  payment_pending:     { label: "Ödeme Bekleniyor",     tone: "amber",   icon: AlertTriangle },
  frozen:              { label: "Abonelik Donduruldu",  tone: "rose",    icon: Ban },
  expired:             { label: "Abonelik Sona Erdi",   tone: "rose",    icon: Ban },
  internal_free:       { label: "Dahili / Ücretsiz Hesap", tone: "slate", icon: CheckCircle2 },
};

const TONE_CLASSES = {
  amber:   "bg-amber-50 text-amber-800 border-amber-200",
  rose:    "bg-rose-50 text-rose-800 border-rose-200",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  slate:   "bg-slate-100 text-slate-700 border-slate-200",
};

/**
 * Ayarlar sayfası "Abonelik" kartı.  Backend `/api/subscription` üzerinden
 * mevcut firmanın abonelik durumunu okur ve duruma göre uygun aksiyonları
 * gösterir (Şimdi Abone Ol / Ödeme Yap / Aboneliği İptal Et / İptal
 * Talebini Geri Al).
 */
export default function SubscriptionCard({ onRefreshAuth }) {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/subscription");
      setSub(data);
    } catch {
      toast.error("Abonelik bilgileri yüklenemedi.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const eff = sub?.effective_status || "trial";
  const meta = STATUS_META[eff] || STATUS_META.trial;
  const Icon = meta.icon;

  const doCancel = async () => {
    setActing(true);
    try {
      const { data } = await api.post("/subscription/cancel");
      setSub(data);
      setConfirmOpen(false);
      toast.success("İptal talebiniz alındı. Aboneliğiniz dönem sonunda sona erecek.");
      if (onRefreshAuth) await onRefreshAuth();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İptal işlemi yapılamadı.");
    } finally { setActing(false); }
  };

  const doUndo = async () => {
    setActing(true);
    try {
      const { data } = await api.post("/subscription/cancel/undo");
      setSub(data);
      toast.success("İptal talebiniz geri alındı.");
      if (onRefreshAuth) await onRefreshAuth();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız.");
    } finally { setActing(false); }
  };

  return (
    <SectionCard title="Abonelik" testId="subscription-card">
      <div className="p-5 space-y-5" data-testid="subscription-card-body">
        {loading && (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor…
          </div>
        )}

        {!loading && sub && (
          <>
            {/* Durum rozeti */}
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${TONE_CLASSES[meta.tone]}`}
                 data-testid="subscription-status-badge">
              <Icon className="w-3.5 h-3.5" />
              {meta.label}
            </div>

            {/* Alan ızgarası */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {eff === "internal_free" ? (
                <div className="col-span-full text-slate-600">
                  Dahili/ücretsiz hesap. Ödeme ve iptal işlemleri geçerli değildir.
                </div>
              ) : (
                <>
                  {eff === "trial" || eff === "trial_expired" ? (
                    <>
                      <Field label="Deneme Başlangıç" value={fmtDate(sub.trial_start_date)} />
                      <Field label="Deneme Bitiş"     value={fmtDate(sub.trial_end_date)} />
                      <Field label="Kalan Gün"        value={sub.trial_days_left ?? "—"} />
                      <Field label="Seçili Plan"      value={sub.plan ? PLAN_LABELS[sub.plan] : "Henüz seçilmedi"} />
                    </>
                  ) : (
                    <>
                      <Field label="Plan"                value={sub.plan ? PLAN_LABELS[sub.plan] : "—"} />
                      <Field label="Plan Ücreti"         value={sub.plan ? PLAN_PRICES[sub.plan] : "—"} />
                      <Field label="Abonelik Başlangıç"  value={fmtDate(sub.subscription_start_date)} />
                      <Field label="Son Ödeme"           value={fmtDate(sub.last_payment_date)} />
                      <Field label="Dönem Bitişi"        value={fmtDate(sub.subscription_end_date)} />
                      {typeof sub.auto_renew === "boolean" && (
                        <Field label="Otomatik Yenileme" value={sub.auto_renew ? "Açık" : "Kapalı"} />
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {/* Durum mesajları */}
            {eff === "cancel_at_period_end" && (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5"
                   data-testid="subscription-cancel-notice">
                Aboneliğiniz <strong>{fmtDate(sub.subscription_end_date)}</strong> tarihinde sona erecektir. Bu tarihe kadar Penz&apos;i kullanmaya devam edebilirsiniz.
              </div>
            )}
            {eff === "payment_pending" && (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5"
                   data-testid="subscription-grace-notice">
                Ödemeniz alınamadı. Aboneliğiniz <strong>{fmtDate(sub.grace_period_end)}</strong> tarihine kadar aktif kalacaktır. Bu tarihe kadar ödeme yapılmazsa hesabınız dondurulacaktır.
              </div>
            )}
            {eff === "frozen" && (
              <div className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5">
                Ödeme tamamlanmadığı için aboneliğiniz donduruldu. Penz&apos;i kullanmaya devam etmek için lütfen ödemenizi tamamlayın.
              </div>
            )}

            {/* Aksiyonlar */}
            <div className="pt-2 flex flex-wrap gap-2">
              {(eff === "trial" || eff === "trial_expired" || eff === "expired") && (
                <Button
                  onClick={() => navigate("/abonelik")}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="subscribe-now-button"
                >
                  <CreditCard className="w-4 h-4 mr-1.5" /> Şimdi Abone Ol
                </Button>
              )}
              {(eff === "payment_pending" || eff === "frozen") && (
                <Button
                  onClick={() => navigate("/abonelik")}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="complete-payment-button"
                >
                  <CreditCard className="w-4 h-4 mr-1.5" /> Ödemeyi Tamamla
                </Button>
              )}
              {eff === "paid" && (
                <Button
                  variant="outline"
                  onClick={() => setConfirmOpen(true)}
                  data-testid="cancel-subscription-button"
                >
                  Aboneliği İptal Et
                </Button>
              )}
              {eff === "cancel_at_period_end" && (
                <Button
                  onClick={doUndo}
                  disabled={acting}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="undo-cancel-button"
                >
                  {acting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                  İptal Talebini Geri Al
                </Button>
              )}
            </div>
          </>
        )}

        {/* İptal onay diyaloğu */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent data-testid="cancel-confirm-dialog">
            <DialogHeader>
              <DialogTitle>Aboneliği iptal etmek istediğinize emin misiniz?</DialogTitle>
              <DialogDescription className="pt-2 text-sm leading-relaxed text-slate-600">
                Aboneliğiniz mevcut ödeme döneminizin sonunda sona erecektir. Bu tarihe kadar Penz&apos;i kullanmaya devam edebilirsiniz. Yıllık aboneliklerde kullanılmayan dönem için ücret iadesi yapılmaz.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="cancel-confirm-abort">Vazgeç</Button>
              <Button
                onClick={doCancel}
                disabled={acting}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="cancel-confirm-approve"
              >
                {acting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                İptali Onayla
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SectionCard>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold">{label}</div>
      <div className="mt-0.5 text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}
